// Shared bits of the merge-request pages (spec/dashboard.md section 12.3; spec/api-conventions.md sections 12.2, 12.3):
// the cache keys, the state words and tones, why a request is not mergeable, what the Merge button waits for.
import { ApiError } from '../../api/client';
import type { MergeRequest } from '../../api/types';
import type { Tone } from '../../ui/Chip';
import { gameKey } from '../../shell/GameContext';

export const PAGE_SIZE = 50;

export const STATES = ['open', 'merged', 'closed', 'all'] as const;
export type StateFilter = (typeof STATES)[number];

export const STATE_TONE: Record<string, Tone> = { open: 'info', merged: 'success', closed: 'neutral' };

/** The prefix of every merge-request read of the game: invalidating it re-reads the list, the request, its diff and conflicts. */
export function mrPrefix(org: string, game: string): string {
  return gameKey(org, game, 'merge-requests');
}

export function mrKey(org: string, game: string, mr: string, what = ''): string {
  return `${mrPrefix(org, game)}:mr:${mr}${what ? `:${what}` : ''}`;
}

export function plural(n: number | string, word: string): string {
  return `${n} ${word}${Number(n) === 1 ? '' : 's'}`;
}

export function asApiError(e: unknown): ApiError {
  return e instanceof ApiError ? e : new ApiError(0, 'unavailable', e instanceof Error ? e.message : String(e), '');
}

export function shortSha(sha: string | null | undefined): string {
  return sha ? sha.slice(0, 8) : '—';
}

export function currentApprovals(mr: MergeRequest): number {
  return (mr.approvals ?? []).filter((a) => a.current).length;
}

/** Why the request cannot be merged (empty when it can): the state, the unresolved conflicts, the protected target's approval. */
export function notMergeableReasons(mr: MergeRequest): string[] {
  if (mr.state !== 'open') return [mr.state === 'merged' ? 'the request is merged' : 'the request is closed'];
  const out: string[] = [];
  const unresolved = Number(mr.unresolved);
  if (unresolved > 0) out.push(plural(unresolved, 'unresolved conflict'));
  if (mr.targetProtected && !mr.approved) out.push('the protected target waits for an approval');
  if (out.length === 0 && !mr.mergeable) out.push('the server reports it not mergeable');
  return out;
}

/** The Merge button's disabled reason (null when the request is mergeable). */
export function mergeBlock(mr: MergeRequest): string | null {
  if (mr.mergeable && mr.state === 'open') return null;
  if (mr.state !== 'open') return `The request is ${mr.state}.`;
  if (Number(mr.unresolved) > 0) return 'Not mergeable: resolve the conflicts first.';
  if (mr.targetProtected && !mr.approved) return 'Not mergeable: the protected target needs a current approval.';
  return 'Not mergeable.';
}
